const _ = require('lodash')
const moment = require('moment-timezone')
const numeral = require('numeral')

const DATE_FORMAT = 'MMM D Y'
const TIME_FORMAT = 'hh:mma'
const LOW_BALANCE_WARNING = 500
const ZERO_OR_NEGATIVE_BALANCE_THRESHOLD = 0

const AGENCIES = [
  { text: 'SmartTransit Ghana', value: 'STRANSITGHA', metadata: { countryId: 'GHA' } }
]

function generateKeywordsQueryForOr (keywordsString, fields) {
  let queries = []
  let keywords = keywordsString.trim().replace(/[\s]{2,}/g, ' ').split(' ')
  fields.forEach((field, index) => {
    if (keywords.length === 1) {
      queries.push({ [field]: { ilike: `%${keywords[0]}%` } })
    } else if (keywords.length > 1) {
      queries.push({ and: [] })
      keywords.forEach((keyword) => {
        queries[index].and.push({ [field]: { ilike: `%${keyword}%` } })
      })
    }
  })
  return queries.length ? queries : undefined
}

function formatCurrency (amount, currencyPrefix = '', customFormat = null) {
  const parsedAmount = ((amount + '').indexOf('.') === -1) ? _.round((numeral(amount).value() / 100), 2) : numeral(amount).value()
  return (currencyPrefix ? currencyPrefix + ' ' : '') + numeral(parsedAmount).format(customFormat || '0,0.00')
}

function convertToDecimalFormat (amount) {
  return ((amount + '').indexOf('.') === -1) ? _.round((numeral(amount).value() / 100), 2) : numeral(amount).value()
}

function formatDate (date, timezone, { displayTimezone = false } = {}) {
  const momentDate = moment.isMoment(date) ? date : (timezone ? moment(date).tz(timezone) : moment(date))
  return momentDate.format(DATE_FORMAT + (displayTimezone ? ' z' : ''))
}

function formatTime (time, timezone, { displayTimezone = false } = {}) {
  let momentDate

  if (moment.isMoment(time)) {
    if (timezone) {
      momentDate = time.tz(timezone)
    } else {
      momentDate = time
    }
  } else if (typeof time === 'string' && /^[0-9]{1,2}:[0-9]{2}$/u.test(time)) {
    // Time provided as "HH:mm" (e.g. "08:00") – let moment parse that as a time
    // and turn it into a Date, optionally in the provided timezone.
    if (timezone) {
      momentDate = moment.tz(time, 'HH:mm', timezone)
    } else {
      momentDate = moment(time, 'HH:mm')
    }
  } else {
    momentDate = timezone ? moment(time).tz(timezone) : moment(time)
  }

  return momentDate.format(TIME_FORMAT + (displayTimezone ? ' z' : ''))
}

function sleep (msecs) {
  return new Promise((resolve) => setTimeout(() => resolve(), msecs))
}

function getShortTransactionLogId (transactionLogId) {
  return transactionLogId.substring(transactionLogId.length - 7).toUpperCase()
}

function getPhonePrefixByCountryId (countryId) {
  switch (countryId) {
    case 'GHA':
      return '+233'
  }
}

function getTimezoneByCountryId (countryId) {
  switch (countryId) {
    case 'GHA':
      return 'Africa/Accra'
    case 'CIV':
      return 'Africa/Abidjan'
    case 'NGA':
      return 'Africa/Lagos'
    default:
      return 'Africa/Accra'
  }
}

function isPhoneNumberValid (phoneNumber) {
  // Clean out common phone number separators
  const cleanedNumber = String(phoneNumber || '').replace(/[()\-\s]+/g, '')

  if (!cleanedNumber) {
    return false
  }

  // Ghana numbers are matched strictly so malformed Ghana inputs (e.g. an extra
  // digit) cannot leak through the looser "other international" rule below.
  // Callers are expected to normalize local-format numbers (e.g. 0XXXXXXXXX) to
  // the international form (+233XXXXXXXXX or 233XXXXXXXXX) before validating.
  if (/^\+?233/.test(cleanedNumber)) {
    return /^\+?233\d{9}$/.test(cleanedNumber)
  }

  // US/Canada number: +1 followed by exactly 10 digits
  if (cleanedNumber.match(/^\+1\d{10}$/)) {
    return true
  }

  // UK number: +44 followed by exactly 10 digits
  if (cleanedNumber.match(/^\+44\d{10}$/)) {
    return true
  }

  // Other international numbers: + followed by 10 to 15 digits (E.164 max is 15)
  if (cleanedNumber.match(/^\+\d{10,15}$/)) {
    return true
  }

  return false
}

/**
 * Convenience function to return the valid support contacts based on phone country code
 * @param {string} phoneCountryCode - e.g. '+233'
 * @returns {{type:('email'|'whatsapp'), value: string}[]}
 */
function getSupportContacts (phoneCountryCode) {
  switch (phoneCountryCode) {
    case '+233':
      return [
        { type: 'email', label: 'support@smarttransyt.com', value: 'support@smarttransyt.com' },
        { type: 'whatsapp', label: '+233 53 102 2770', value: 'https://wa.me/+233531022770' },
        { type: 'phone', label: '053 102 2770 / +233 53 102 2770', value: '+233531022770' }
      ]
    case '+1':
    case '+44':
    default:
      return [
        { type: 'email', label: 'support@smarttransyt.com', value: 'support@smarttransyt.com' },
        { type: 'whatsapp', label: '+233 53 102 2770', value: 'https://wa.me/+233531022770' }
      ]
  }
}

const { getTimezoneByAgencyId } = require('./agency-timezones')

function convertSecondsToHoursMinutes (seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secondsRemaining = seconds % 60

  return { hours, minutes, seconds: secondsRemaining }
}

function formatHoursMinutes ({ hours, minutes, seconds, hrLabel = 'hr', minLabel = 'min', secLabel = 'sec' }) {
  return [
    hours ? hours + hrLabel : '',
    minutes ? minutes + minLabel : '',
    seconds && !hours && !minutes ? seconds + secLabel : ''
  ].filter(Boolean).join(' ')
}

module.exports = {
  formatCurrency,
  convertToDecimalFormat,
  formatDate,
  formatTime,
  getPhonePrefixByCountryId,
  sleep,
  getShortTransactionLogId,
  getSupportContacts,
  generateKeywordsQueryForOr,
  convertSecondsToHoursMinutes,
  formatHoursMinutes,
  AGENCIES: AGENCIES.slice(0),
  LOW_BALANCE_WARNING,
  ZERO_OR_NEGATIVE_BALANCE_THRESHOLD,
  isPhoneNumberValid,
  getTimezoneByAgencyId,
  getTimezoneByCountryId
}
